import { useId } from "react";

import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AJUSTE_POR_FUENTE,
  ajustesEfectivos,
  correccionTotal,
  fuenteDeLaClase,
  porcentajeDeAjuste,
  valorHomologado,
  type AjusteDeHomologacion,
} from "@/lib/homologacion";
import { numero } from "@/lib/formatoDeMercado";
import type { AjusteDeReferencia, ClaseDeEvidencia } from "@/lib/referencias";

/**
 * El valor del selector cuando el tasador todavía no juzgó esta diferencia.
 *
 * Es un centinela y no la cadena vacía porque Radix reserva `""` para "sin
 * valor" y se niega a montar un `SelectItem` con ese valor. Nunca sale de este
 * archivo: al guardar se traduce a la AUSENCIA del ajuste en la lista.
 */
const SIN_SELECCIONAR = "sin-seleccionar";

interface AjustesDeHomologacionProps {
  /** El catálogo vivo: los cinco ajustes con su escala. */
  escala: AjusteDeHomologacion[];
  /** Lo que esta ficha ya tiene congelado. */
  elegidos: AjusteDeReferencia[];
  /**
   * La clase de evidencia de la ficha, o sea la PESTAÑA. De acá sale el ajuste
   * por fuente, que no se pregunta.
   */
  clase: ClaseDeEvidencia;
  /**
   * La lista ENTERA de categorías elegidas, en el orden del catálogo. Es el
   * estado final, no un incremento, y NUNCA incluye la fuente.
   */
  onCambiar: (categoriaIds: number[]) => void;
  deshabilitado?: boolean;
  /**
   * El valor unitario del comparable, en UF/ha, sobre el que se aplica la
   * corrección. `null` cuando no se pudo calcular.
   *
   * ES UF/HA Y NO EL PRECIO TOTAL porque es la cifra con la que se comparan
   * predios de distinto tamaño — la misma que resume el panel y la que dibuja
   * el gráfico del catálogo.
   */
  ufHa: number | null;
}

/**
 * Los cinco ajustes con que se homologa un comparable contra el predio tasado.
 *
 * Homologar es corregir el valor de la ficha por cada diferencia con el predio
 * —de dónde viene, cuánto mide, dónde está, qué potencial tiene, qué forma—
 * hasta dejarla comparable. La escala la fija el mantenedor
 * (`/admin/homologacion`); acá se elige un peldaño por ajuste.
 *
 * VIVE EN LA FICHA DEL INFORME Y NO EN EL SELECTOR DE REFERENCIAS, y la
 * separación la fijó el stakeholder (07-09-2026): aquel módulo ELIGE los
 * comparables y este capítulo los GESTIONA.
 *
 * SON CINCO AJUSTES Y CUATRO SELECTORES. El de FUENTE no se pregunta: sus
 * categorías —CBR y Oferta— salen de la clase de evidencia, que es la pestaña
 * en la que el tasador está parado. Se MUESTRA igual, porque entra al cálculo y
 * esconderlo dejaría sin explicar por qué el valor corregido no cuadra con los
 * cuatro que sí se eligieron.
 *
 * LO QUE SE MUESTRA ES LO CONGELADO, no lo que hoy dice el catálogo. Si alguien
 * corrige la escala después, el informe sigue llevando el porcentaje del día en
 * que se eligió —lo copió `AjustadorDeReferencia`— así que mostrar el vivo
 * pondría en pantalla un número que ese informe no usa. Las OPCIONES sí son las
 * vivas: son las que se aplicarían al volver a elegir.
 */
export function AjustesDeHomologacion({
  escala,
  elegidos,
  clase,
  onCambiar,
  deshabilitado = false,
  ufHa,
}: AjustesDeHomologacionProps) {
  const base = useId();

  // El catálogo todavía no llegó, o falló. Cinco controles sin opciones son
  // cinco cosas que no se pueden usar y que igual ocupan la ficha; la
  // referencia y sus cifras se siguen viendo, que es lo que importa.
  if (escala.length === 0) return null;

  /* LO QUE DE VERDAD CORRIGE incluye la fuente derivada aunque el servidor
     todavía no la haya escrito: sin eso, la ficha mostraría "Oferta −10 %"
     arriba y una corrección total de 0 % abajo. */
  const efectivos = ajustesEfectivos(elegidos, escala, clase);
  const correccion = correccionTotal(efectivos);
  const corregido = valorHomologado(ufHa, efectivos);

  // La línea de arriba usa `border-divisor` y no `border-border`: separa DENTRO
  // de la tarjeta, con el mismo token con que `FichaDeComparable` parte sus
  // secciones. El del marco pesa más y competiría con el borde de la ficha.
  return (
    <>
    <div className="border-divisor mt-3 grid gap-3 border-t pt-3 sm:grid-cols-2 lg:grid-cols-4">
      {escala.map((ajuste) => {
        const rotuloId = `${base}-${ajuste.codigo}`;

        /* EL DE FUENTE SE RESUELVE, NO SE OFRECE. Va en el mismo lugar del
           orden —primero, como en el método de tasación— y con el mismo rótulo,
           para que la fila de cinco se lea pareja. */
        if (ajuste.codigo === AJUSTE_POR_FUENTE) {
          const resuelta = fuenteDeLaClase(escala, clase);

          return (
            <div key={ajuste.id} className="flex flex-col gap-1.5">
              <RotuloDeFiltro id={rotuloId}>{ajuste.nombre}</RotuloDeFiltro>

              {/* La altura del selector de al lado (`h-11`), para que los cinco
                  compartan línea de base. Sin borde ni fondo: no es un control
                  apagado —que se leería como algo que se podría tocar— sino un
                  dato que el sistema ya sabe. */}
              <p
                aria-labelledby={rotuloId}
                className="text-ink flex h-11 items-center text-sm font-semibold"
              >
                {resuelta === null
                  ? // Sin la clase declarada no se elige una al azar: el
                    // porcentaje equivocado entraría al cálculo sin que nada
                    // falle. Es el catálogo de una instalación sin sembrar.
                    "—"
                  : `${resuelta.categoria} · ${porcentajeDeAjuste(resuelta.porcentaje)}`}
              </p>
            </div>
          );
        }

        const elegido = elegidos.find((puesto) => puesto.ajuste_id === ajuste.id);

        return (
          <div key={ajuste.id} className="flex flex-col gap-1.5">
            {/* `id` y `aria-labelledby` en vez de `htmlFor`: un `combobox` no
                toma su nombre del contenido, así que un rótulo suelto se ve en
                pantalla y deja el control mudo — y acá hay varios seguidos. */}
            <RotuloDeFiltro id={rotuloId}>{ajuste.nombre}</RotuloDeFiltro>

            <Select
              value={elegido ? String(elegido.categoria_id) : SIN_SELECCIONAR}
              onValueChange={(valor) =>
                onCambiar(
                  categoriasTrasElegir(
                    escala,
                    elegidos,
                    ajuste.id,
                    valor === SIN_SELECCIONAR ? null : Number(valor),
                  ),
                )
              }
              disabled={deshabilitado}
            >
              <SelectTrigger aria-labelledby={rotuloId} className="w-full">
                <SelectValue>
                  {elegido
                    ? `${elegido.categoria} · ${porcentajeDeAjuste(elegido.porcentaje)}`
                    : "Sin seleccionar"}
                </SelectValue>
              </SelectTrigger>

              <SelectContent>
                {/* QUITAR EL AJUSTE TIENE QUE SER POSIBLE, y no es lo mismo que
                    elegir el peldaño de 0 %: ese afirma que el tasador juzgó
                    que no hay diferencia. */}
                <SelectItem value={SIN_SELECCIONAR}>Sin seleccionar</SelectItem>

                {ajuste.categorias.map((categoria) => (
                  <SelectItem key={categoria.id} value={String(categoria.id)}>
                    {categoria.categoria} · {porcentajeDeAjuste(categoria.porcentaje)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>

    {/* EL RESULTADO, que es para lo que se eligieron los cinco. Sin esta línea
        el tasador mueve porcentajes y no ve el número que sale de ellos.

        Va en su propia franja y no como una sexta celda de la grilla: no es un
        ajuste más, es lo que los ajustes producen. */}
    <div className="border-divisor bg-superficie-sutil mt-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 rounded-xl border-[1.5px] px-3.5 py-2.5">
      <p className="flex items-baseline gap-2">
        <span className="text-ink-hint text-[0.625rem] font-extrabold tracking-[0.05em] uppercase">
          Corrección total
        </span>
        <span className="text-ink font-mono text-sm font-bold tabular-nums">
          {porcentajeDeAjuste(correccion)}
        </span>
      </p>

      <p className="flex items-baseline gap-2">
        <span className="text-ink-hint text-[0.625rem] font-extrabold tracking-[0.05em] uppercase">
          UF/ha homologado
        </span>
        {/* Verde: es una cifra DERIVADA, no un dato leído del catálogo. Mismo
            criterio con que el panel de selección marca el valor del predio. */}
        <span className="text-success-soft-foreground font-mono text-sm font-bold tabular-nums">
          {corregido === null ? "—" : numero(corregido, 0)}
        </span>
      </p>
    </div>
    </>
  );
}

/**
 * La lista completa de categorías tras cambiar UNA, en el orden del catálogo.
 *
 * SE RECORRE LA ESCALA Y NO LO ELEGIDO, así el orden es estable y no depende de
 * en qué secuencia se fueron marcando los selectores. Un ajuste que ya no está
 * en el catálogo queda fuera a propósito: no tiene control que lo muestre, así
 * que conservarlo mandaría un id que nadie puede ver ni deshacer.
 *
 * LA FUENTE NUNCA VIAJA. La pone el servidor, y su request RECHAZA con 422 una
 * que venga en el cuerpo: mandarla dejaría de poder guardar cualquier ajuste.
 */
function categoriasTrasElegir(
  escala: AjusteDeHomologacion[],
  elegidos: AjusteDeReferencia[],
  ajusteId: number,
  categoriaId: number | null,
): number[] {
  return escala
    .filter((ajuste) => ajuste.codigo !== AJUSTE_POR_FUENTE)
    .map((ajuste) =>
      ajuste.id === ajusteId
        ? categoriaId
        : (elegidos.find((puesto) => puesto.ajuste_id === ajuste.id)?.categoria_id ?? null),
    )
    .filter((id): id is number => id !== null);
}
